// Basic
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import PropTypes from 'prop-types';
// Libs
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
// Store & actions
import actionsCustomization from '../../api/actionsCustomization';
// Components
import HeaderTitle from '../../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '../../../../../../common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Entity from '../../../../../../common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '../../../../../../common-utils/components/ui/modal';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/common-utils/components/ui/form';
import { Input } from '@/common-utils/components/ui/input';
// Common
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsCustomization } from '@/app/i18n/namespaces';

/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @param root0.customization
 * @param root0.setLaunchRefetch
 * @returns {JSX} ModalUpdateLabel
 */
function ModalUpdateLabel({ isOpen, setIsOpen, customization, setLaunchRefetch }) {
    // Toast
    const { toast } = useToast();

    // Form
    const formSchema = z.object({
        label: z.string()
            .nonempty()
            .min(1, '') // At least 1 character
            .max(100, '') // Max 100 characters
    });
    const form = useForm({
        resolver: zodResolver(formSchema),
        mode: 'onChange',
        defaultValues: { label: customization?.packageLabel || '' }
    });

    // State
    const [showSubmitButton, setShowSubmitButton] = useState(true);

    // Api calls
    const onSuccess = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        setLaunchRefetch(true);
        toast({
            description: <Entity entity='modal.update_label.success' namespace={nsCustomization} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setShowSubmitButton(false);
        setIsOpen(false);
        toast({
            description: <Entity entity='modal.update_label.error' namespace={nsCustomization} />,
            variant: 'danger'
        });
    };
    const submitUpdateLabel = actionsCustomization.updateLabel(onSuccess, onError);

    // Functions
    const onSubmit = (data) => {
        submitUpdateLabel.mutate({ uuid: customization.uuid, packageLabel: data.label });
    };
    const onClose = () => {
        setIsOpen(false);
        if (submitUpdateLabel.isSuccess) {
            setLaunchRefetch(true);
        }
    };

    return <Modal open={isOpen} onOpenChange={onClose}>
        <ModalContent className='max-w-lg'>
            <HeaderTitle title='modal.update_label.title' namespace={nsCustomization} />
            <ModalBody>
                <Form {...form} onSubmit={form.handleSubmit(onSubmit)}>
                    <FormLabel htmlFor='label' className='block text-sm font-medium text-gray-700'>
                        <Entity entity='modal.update_label.label' namespace={nsCustomization} />
                    </FormLabel>
                    <FormField
                        name='label'
                        control={form.control}
                        render={({ field }) => (
                            <FormItem className='addresses-list-input'>
                                <FormControl>
                                    <Input
                                        {...field}
                                        autoFocus
                                        type='text'
                                        entity='modal.update_label.placeholder'
                                        namespace={nsCustomization}
                                        autoComplete='off'
                                        value={field.value}
                                        onChange={(e) => field.onChange(e.target.value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </Form>
            </ModalBody>
            <FooterForm
                onSubmit={form.handleSubmit(onSubmit)}
                isSubmitting={submitUpdateLabel.isLoading}
                showSubmitButton={showSubmitButton}
                isSubmitDisabled={!form.formState.isValid}
            />
        </ModalContent>
    </Modal>;
}

ModalUpdateLabel.propTypes = {
    setLaunchRefetch: PropTypes.func,
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    customization: PropTypes.object.isRequired
};

export default ModalUpdateLabel;